/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import fitty from 'fitty';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { objectIsEmpty } from 'utils/objectEmpty';
import { useDispatch, useSelector } from 'react-redux';

import {
  addProductToCart,
  updateQuantityOfProductInCart,
} from 'store/modules/cart';
import { IState } from 'store';
import api from 'services/api';
import { useNotices } from 'hooks';
import {
  createDebouncedDecrementHandler,
  createIncrementHandler,
} from 'utils/itemCounterFunctions';
import { formatCurrency } from 'utils/format';
import { IProductSelosProps } from 'utils/cards';
import { getGACartItemsParams } from 'utils/gtag';
import { IProductCarouselProps } from 'store/modules/product';
import { IConfigApplicationProps } from 'store/modules/config';
import { IUserProps, removeFavorite, setFavorite } from 'store/modules/profile';

import Button from 'components/common/Button';
import CardsList from 'components/common/CardsList';
import ItemCounter from 'components/pages/PDP/ItemCounter';
import { SendMeEmail } from 'components/pages/PDP/SendMeEmail';
import CardsListMobile from 'components/common/CardsListMobile';

import ProductImage from './ProductImage';

import * as S from './styles';

interface IProductItemProps {
  item: IProductCarouselProps & {
    preco_Venda?: number;
    preco_Vigente?: number;
  };
  componenteVitrineId?: number;
}

const ProductItem: React.FC<IProductItemProps> = ({
  item,
  componenteVitrineId,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const { addNotices } = useNotices();
  const installmentRef = useRef(null);
  const installmentInstance = useRef(null);

  const user = useSelector<IState, IUserProps>(state => state.profile.user);
  const { ultraDescontao } = useSelector<IState, IConfigApplicationProps>(
    state => state.config.config,
  );

  const [quantity, setQuantity] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState('web');
  const [isFavorite, setIsFavorite] = useState(item.favorito);
  const [showModalLetMeKnow, setShowModalLetMeKnow] = useState(false);

  useEffect(() => {
    if (router.asPath.includes('/central-do-cliente/favoritos')) {
      setIsFavorite(true);
    }
  }, [router]);

  useEffect(() => {
    function resizeWindow() {
      if (typeof window !== 'undefined') {
        if (window.innerWidth > 580) {
          setWindowWidth('web');
        } else {
          setWindowWidth('mobile');
        }

        const isSmall = window.innerWidth <= 470;

        if (installmentRef.current) {
          // Se já existe uma instância, desinscreve para evitar duplicações
          if (installmentInstance.current) {
            installmentInstance.current.unsubscribe();
          }

          // Cria nova instância
          installmentInstance.current = fitty(installmentRef.current, {
            minSize: 10,
            maxSize: 16.8,
            multiLine: isSmall,
          });
        }
      }
    }

    resizeWindow();

    window.addEventListener('resize', resizeWindow);

    return () => {
      window.removeEventListener('resize', resizeWindow);

      if (installmentInstance.current) {
        installmentInstance.current.unsubscribe();
      }
    };
  }, []);

  const productSelos: IProductSelosProps = useMemo(() => {
    const descontoProgressivo =
      item.aplicarPromocao && item.promocaoId === 38 ? true : false;

    const importado = item.importado ? `importado${item.seloImportado}` : null;

    return {
      freteGratis: item.freteGratis,
      ultraDesconto: item.ultraDesconto && ultraDescontao?.estaVigente,
      descontoProgressivo: descontoProgressivo,
      possuiGenerico: item.possuiGenerico,
      bloqueadoVendaSite:
        item.bloqueadoVendaSite || item.possuiAlgumaRestricaoVenda,
      possuiBrinde: item.possuiBrinde,
      ultraCompare: item.ultraCompare,
      importado: importado,
      ultraPremio: item.ultraPremio,
      seloLivre: item.seloLivre,
      cliqueRetire: item.cliqueRetire,
    };
  }, [item, ultraDescontao]);

  const haveSelo = Object.values(productSelos).some(valor => Boolean(valor));

  const handleFavoriteProduct = useCallback(() => {
    if (!user || objectIsEmpty(user)) {
      router.push(`/identificacao?ReturnUrl=/${item.permalink}`);
    } else if (isFavorite) {
      addNotices({
        type: 'warn',
        messages: [`Deseja excluir ${item.nome} dos seus favoritos?`],
        needConfirmation: true,
        action: () => {
          dispatch(
            removeFavorite({ productId: item.produtoId, sku: item.permalink }),
          );

          setIsFavorite(false);

          setTimeout(() => {
            addNotices({
              type: 'success',
              messages: [`${item.nome} foi removido dos seus favoritos!`],
            });
          }, 1000);
        },
      });
    } else {
      dispatch(
        setFavorite({
          clientId: user.id,
          productId: item.id,
          sku: item.permalink,
        }),
      );

      setIsFavorite(true);

      addNotices({
        type: 'success',
        messages: [`${item.nome} foi salvo nos seus favoritos!`],
      });
    }
  }, [item, isFavorite]);

  const handleLetMeKnow = useCallback(async () => {
    try {
      const response = await api.post('aviseme', {
        email: user.email,
        sku: item.sku,
        nome: user.nomeCompleto,
      });

      if (!response.data.contemAvisos && !response.data.contemErros) {
        setShowModalLetMeKnow(false);

        addNotices({
          type: 'success',
          messages: [
            'Registramos sua solicitação, assim que o produto voltar ao estoque entraremos em contato!',
          ],
        });
      }
    } catch (err) {
      if (err?.data) {
        addNotices({ messages: [err.data.erros], type: 'errors' });
      }
    }
  }, [user]);

  const handleBuyProduct = useCallback(() => {
    router.push(`/${item.permalink}`);
  }, [item]);

  const handleAddProduct = useCallback(
    (showAfterRequest: boolean) => {
      dispatch(
        addProductToCart({
          quantity: quantity,
          sku: item.codigo,
          showAfterRequest,
          GA: getGACartItemsParams({ product: item, qty: 1 }),
          componenteVitrineId,
        }),
      );
    },
    [componenteVitrineId, dispatch, item, quantity],
  );

  const handleAddProductAndPush = useCallback(() => {
    handleAddProduct(false);

    router.push('/checkout/carrinho');
  }, [componenteVitrineId, dispatch, item, quantity, router]);

  const handleDecrementItem = useMemo(
    () =>
      createDebouncedDecrementHandler({
        quantity,
        item,
        dispatch,
        timeoutRef,
      }),
    [quantity, item, dispatch],
  );

  const handleIncrementItem = useMemo(
    () =>
      createIncrementHandler({
        item,
        dispatch,
      }),
    [item, dispatch],
  );

  return (
    <>
      <S.Container
        className={`product-item${
          item.freteGratis ? ' have-freight-free' : ''
        }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {router.asPath.includes('/central-do-cliente/favoritos') && (
          <>
            {isFavorite ? (
              <img
                src="/images/icons/trash-black.svg"
                alt="Coração"
                className="icon heart-icon"
                onClick={handleFavoriteProduct}
              />
            ) : (
              <img
                src="/images/icons/heart.svg"
                alt="Coração"
                className="icon heart-icon"
                onClick={handleFavoriteProduct}
              />
            )}
          </>
        )}

        <Link
          href={{
            pathname: `/${item.permalink}`,
            query: { cvi: componenteVitrineId },
          }}
        >
          <a
            className={`product${
              item.disponivel ? '-available' : '-unavailable'
            }`}
          >
            <CardsList hovered={hovered} productSelos={productSelos} />

            {item.freteGratis && (
              <img
                src="/images/card-icons/frete-gratis-tag.svg"
                alt="Frete Grátis"
                className="frete-gratis-tag"
              />
            )}

            {!router.asPath.includes('/central-do-cliente/favoritos') && (
              <>
                {item.novidade && (
                  <span className="tag tag-novidade">Novidade</span>
                )}
              </>
            )}

            {/* <>
              {!!item.desconto && (
                <div className="tag-discount">
                  <span>-{item.desconto}%</span>
                </div>
              )}
            </> */}

            <ProductImage item={item} haveSelo={haveSelo} />

            <CardsListMobile productSelos={productSelos} />

            <div className="product-item-tarjas">
              {/* {item.freteGratis && (
                <span className="tarjas frete-gratis-tarja">Frete Grátis</span>
              )} */}
              {/* {item.possuiBrinde && (
                <span className="tarjas ganhe-brinde-tarja">Ganhe brinde</span>
              )} */}
              {item.aplicarPromocao && item.promocaoId !== 38 && (
                <img
                  loading="lazy"
                  src={item.promocaoImagemUrl}
                  alt="Selo promoção"
                  className="promotion-tarja"
                />
              )}
              {item.bloqueadoVendaSite ||
                (item.possuiAlgumaRestricaoVenda && (
                  <span className="tarjas red-tarja">
                    Venda exclusiva em loja física
                  </span>
                ))}
              {item.vendaControlada && (
                <span className="tarjas red-tarja">
                  Somente sob prescrição médica
                </span>
              )}
            </div>

            <div className="product-item-info">
              <span className="product-item-name">{item.nome}</span>

              <div>
                {item.disponivel && (
                  <>
                    {!item.bloqueadoVendaSite ||
                    !item.possuiAlgumaRestricaoVenda ? (
                      <div>
                        <div className="product-item-price-info">
                          <div className="product-item-old-price-info">
                            {(!!item.precoDe || !!item.preco_Venda) && (
                              <>
                                <span className="product-item-old-price">
                                  <s>
                                    {!!item.precoDe
                                      ? item.precoDeTexto
                                      : `R$ ${formatCurrency(
                                          item.preco_Venda,
                                        )}}`}
                                  </s>
                                  {/* <span className="product-item-x-price-old"></span> */}
                                </span>
                                <span className="discount">
                                  {item.desconto
                                    ? item.desconto
                                    : item.desconto}
                                  % off
                                </span>
                              </>
                            )}
                          </div>

                          {item.exibirPrecoUnitarioPromocaoVitrine &&
                            !!item.precoPix && (
                              <div className="product-item-new-price-info">
                                {!!item.precoPor && (
                                  <span className="product-item-new-price">
                                    {item.precoPorTexto}
                                  </span>
                                )}
                              </div>
                            )}

                          {item.exibirPrecoUnitarioPromocaoVitrine &&
                            item.promocaoProgressivaDescricao && (
                              <div className="product-item-price-unity-text">
                                {item.promocaoProgressivaDescricao}
                              </div>
                            )}

                          {item.exibirPrecoUnitarioPromocaoVitrine ? (
                            <div
                              className="product-item-price-unity"
                              ref={installmentRef}
                            >
                              {`R$ ${formatCurrency(
                                item.precoUnitarioPromocao,
                              )}`}

                              {!!item.numeroParcelaPromocao &&
                                item.numeroParcelaPromocao !== 1 && (
                                  <span>
                                    {' '}
                                    em {item.numeroParcelaPromocao || 1}x{' '}
                                    <b>{item.valorParcelaPromocaoTexto}</b> sem
                                    juros
                                  </span>
                                )}
                            </div>
                          ) : (
                            <>
                              {(!!item.precoPix ||
                                (!!item.numeroParcela &&
                                  item.numeroParcela !== 1)) && (
                                <div
                                  className="product-item-price-parcel"
                                  ref={installmentRef}
                                >
                                  {!!item.precoPorTexto
                                    ? item.precoPorTexto
                                    : item.preco_Vigente &&
                                      `R$ ${formatCurrency(
                                        item.preco_Vigente,
                                      )}`}

                                  {!!item.numeroParcela &&
                                    item.numeroParcela !== 1 && (
                                      <>
                                        {' '}
                                        em {item.numeroParcela || 1}x{' '}
                                        <b>{item.valorParcelaTexto}</b> sem
                                        juros
                                      </>
                                    )}
                                </div>
                              )}
                            </>
                          )}

                          {item.precoPix || item.precoPixPromocao ? (
                            <div className="product-item-price-pix">
                              ou{' '}
                              <b>
                                {item.exibirPrecoUnitarioPromocaoVitrine
                                  ? `R$ ${formatCurrency(
                                      item.precoPixPromocao,
                                    )}`
                                  : item.precoPixTexto}
                              </b>{' '}
                              no Pix
                            </div>
                          ) : (
                            <span className="product-item-price-for">
                              {!!item.precoPorTexto
                                ? item.precoPorTexto
                                : item.preco_Vigente &&
                                  `R$ ${formatCurrency(item.preco_Vigente)}`}
                            </span>
                          )}

                          {!!item.descontoPagamento && (
                            <span className="product-item-discount-parcel">
                              ({item.descontoPagamento})
                            </span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="exclusive-product-item">
                        <span>Venda exclusiva nas lojas da Av. Jabaquara</span>
                        <span>Metrô Saúde Ultrafarma - SP</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </a>
        </Link>

        <div className="product-item-button">
          {item.disponivel && item.controleEstoque > 0 ? (
            <>
              {item.naoPossuiRestricaoVenda ? (
                <div className="product-item-group-buttons">
                  <ItemCounter
                    quantityMax={item.quantidadeMaximaVenda}
                    quantityMin={item.quantidadeMinimaVenda}
                    quantity={quantity}
                    setQuantity={setQuantity}
                    showIcon
                    handleIncrementItem={handleIncrementItem}
                    handleDecrementItem={handleDecrementItem}
                  />

                  <Button id="button-buy" onClick={handleBuyProduct}>
                    Comprar
                  </Button>

                  {/* <Button
                    id="button-add-to-cart"
                    onClick={
                      windowWidth === 'web'
                        ? () => handleAddProduct(true)
                        : handleAddProductAndPush
                    }
                  >
                    <img src="/images/icons/basket-green.svg" alt="Cesta" />
                    Adicionar ao carrinho
                  </Button> */}
                </div>
              ) : (
                <Button id="button-exclusive-product">Consultar estoque</Button>
              )}
            </>
          ) : (
            <div className="product-item-group-buttons product-unavailable">
              <Button id="button-unavailable-product" disabled>
                Produto Indisponível
              </Button>
              <Button
                id="button-send-me-email"
                onClick={() => {
                  if (!!user && !objectIsEmpty(user)) {
                    handleLetMeKnow();
                  } else {
                    setShowModalLetMeKnow(true);
                  }
                }}
              >
                Avise-me
              </Button>
            </div>
          )}
        </div>
      </S.Container>

      {showModalLetMeKnow && (
        <SendMeEmail
          setShowModalLetMeKnow={setShowModalLetMeKnow}
          sku={item.sku}
        />
      )}
    </>
  );
};

export default ProductItem;
